import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import {
  EmptyState,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { shortRef, str } from '../vocabulary/refs';
import DescriptionIcon from '@material-ui/icons/Description';
import PersonIcon from '@material-ui/icons/Person';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { fetchPipelineManifest } from './pipelineManifest';
import type { PipelineDescriptor } from './pipelineManifest';

/**
 * The annotation by which a template says "I am how you ask for this Control
 * pipeline". `answerrocket-service` carries `new-service`: a service needs a
 * repository before Control can be asked, and only a template makes one.
 *
 * Every pipeline NO template claims is offered here as a card of its own,
 * opening the generic manifest form at /create/templates/:id. That is the whole
 * mechanism by which a pipeline Control ships tomorrow shows up on this page
 * with no change to this repository (PORTAL.md §8).
 */
export const CONTROL_PIPELINE_ANNOTATION = 'answerrocket.com/control-pipeline';

/** The card type shown for a manifest pipeline, and its filter value. */
const REQUEST_TYPE = 'request';

/**
 * Template browse, matching the Catalog and Marketplace pages.
 *
 * Replaces the scaffolder's stock Templates tab (sub-page:scaffolder/templates)
 * rather than restyling it: the stock list puts its filters in a left column
 * and gives every card a coloured banner of its own, neither of which can be
 * reconciled with the rest of the portal from CSS. Only that one tab is
 * overridden — Tasks, Actions, Template Editor and Templating Extensions are
 * the scaffolder's, untouched.
 */

const useStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    marginBottom: 20,
  },
  search: { flex: '1 1 260px', minWidth: 220 },
  select: { width: 190 },
  // Count and Register travel together at the right end, and stay right-aligned
  // on the row they wrap onto rather than stranding the button on the left.
  barEnd: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginLeft: 'auto',
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },

  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // A rule rather than a filled banner: the page already carries a full-width
  // gradient band, and a second block of gradient inside every card competes
  // with it. Uses --ar-gradient (per-mode) rather than the fixed brand pair of
  // the page band — at 3px on a dark card the band's deep blue end would
  // disappear, while the dark ramp's lighter stops stay legible.
  cardRule: {
    height: 3,
    flexShrink: 0,
    background: 'var(--ar-gradient)',
  },
  cardHeader: {
    position: 'relative',
    padding: '14px 16px 0',
  },
  cardType: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    marginBottom: 3,
  },
  cardTitle: {
    fontSize: '1.0625rem',
    fontWeight: 640,
    letterSpacing: '-0.011em',
    color: theme.palette.primary.main,
    paddingRight: 56,
  },
  headerIcons: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    gap: 2,
  },
  headerIcon: {
    padding: 5,
    color: 'var(--ar-fg-3)',
    '&:hover': { color: 'var(--ar-fg)' },
  },
  starOn: { color: '#e3b341' },
  cardBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  desc: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    color: 'var(--ar-fg-2)',
    fontSize: '0.8125rem',
  },
  meta: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  docsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    fontSize: '0.8125rem',
    borderTop: '1px solid var(--ar-border-soft)',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 16px',
    borderTop: '1px solid var(--ar-border-soft)',
  },
  owner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    fontSize: '0.8125rem',
    color: 'var(--ar-fg-2)',
  },
  ownerName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dim: { color: 'var(--ar-fg-3)', flexShrink: 0 },
}));

const ANY = '__any__';
const STARRED = '__starred__';

/**
 * Every template and every unclaimed Control pipeline, filterable — the second
 * half of the Create hub (./CreateHub), under its featured cards. It has no
 * page chrome of its own: the scaffolder's Create header and tabs are the page.
 */
export const TemplateCatalog = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const [templates, setTemplates] = useState<Entity[]>();
  const [error, setError] = useState<Error>();
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState(ANY);
  const [type, setType] = useState(ANY);
  const [owner, setOwner] = useState(ANY);
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [pipelines, setPipelines] = useState<PipelineDescriptor[]>();
  const [pipelinesError, setPipelinesError] = useState(false);

  useEffect(() => {
    let active = true;
    // Best-effort: a Control outage must not take the template list with it.
    fetchPipelineManifest(discovery, fetchApi)
      .then(p => active && setPipelines(p))
      .catch(() => active && setPipelinesError(true));
    return () => {
      active = false;
    };
  }, [discovery, fetchApi]);

  useEffect(() => {
    let active = true;
    catalogApi
      .getEntities({
        filter: { kind: 'Template' },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'metadata.tags',
          'metadata.annotations',
          'spec.type',
          'spec.owner',
        ],
      })
      .then(res => active && setTemplates(res.items))
      .catch(e => active && setError(e as Error));
    return () => {
      active = false;
    };
  }, [catalogApi]);

  /** Manifest pipelines no template claims — each gets a card. */
  const requests = useMemo(() => {
    if (!templates || !pipelines) return [];
    const claimed = new Set(
      templates
        .map(t => t.metadata.annotations?.[CONTROL_PIPELINE_ANNOTATION])
        .filter(Boolean),
    );
    return pipelines.filter(p => !claimed.has(p.id));
  }, [templates, pipelines]);

  const types = useMemo(
    () =>
      [
        ...new Set([
          ...(templates ?? []).map(t => str(t.spec?.type)).filter(Boolean),
          ...(requests.length ? [REQUEST_TYPE] : []),
        ]),
      ].sort(),
    [templates, requests],
  );
  const owners = useMemo(
    () =>
      [
        ...new Set(
          (templates ?? []).map(t => shortRef(t.spec?.owner)).filter(Boolean),
        ),
      ].sort(),
    [templates],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (templates ?? [])
      .filter(t => (type === ANY ? true : str(t.spec?.type) === type))
      .filter(t => (owner === ANY ? true : shortRef(t.spec?.owner) === owner))
      .filter(t => (scope === STARRED ? isStarredEntity(t) : true))
      .filter(t =>
        q
          ? `${t.metadata.title ?? ''} ${t.metadata.name} ${
              t.metadata.description ?? ''
            } ${(t.metadata.tags ?? []).join(' ')}`
              .toLowerCase()
              .includes(q)
          : true,
      )
      .sort((a, b) =>
        (a.metadata.title ?? a.metadata.name).localeCompare(
          b.metadata.title ?? b.metadata.name,
        ),
      );
  }, [templates, type, owner, scope, query, isStarredEntity]);

  // Same filters as the templates. A request has no owner or star, so either
  // of those filters hides it rather than pretending to match.
  const visibleRequests = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (owner !== ANY || scope === STARRED) return [];
    if (type !== ANY && type !== REQUEST_TYPE) return [];
    return requests.filter(p =>
      q
        ? `${p.title} ${p.id} ${p.summary} ${(p.clouds ?? []).join(' ')}`
            .toLowerCase()
            .includes(q)
        : true,
    );
  }, [requests, query, owner, scope, type]);

  const loading = !templates && !error;
  const total = visible.length + visibleRequests.length;
  const selectProps = {
    select: true,
    variant: 'outlined' as const,
    size: 'small' as const,
  };

  return (
    <>
      {loading && <Progress />}
      {error && <ResponseErrorPanel error={error} />}

      {templates && (
        <>
          <div className={classes.filterBar}>
            <TextField
              className={classes.search}
              variant="outlined"
              size="small"
              placeholder="Search templates"
              value={query}
              onChange={e => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              {...selectProps}
              className={classes.select}
              label="Show"
              value={scope}
              onChange={e => setScope(e.target.value)}
            >
              <MenuItem value={ANY}>All</MenuItem>
              <MenuItem value={STARRED}>Starred</MenuItem>
            </TextField>
            {types.length > 1 && (
              <TextField
                {...selectProps}
                className={classes.select}
                label="Type"
                value={type}
                onChange={e => setType(e.target.value)}
              >
                <MenuItem value={ANY}>All types</MenuItem>
                {types.map(t => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {owners.length > 1 && (
              <TextField
                {...selectProps}
                className={classes.select}
                label="Owner"
                value={owner}
                onChange={e => setOwner(e.target.value)}
              >
                <MenuItem value={ANY}>All owners</MenuItem>
                {owners.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <div className={classes.barEnd}>
              <span className={classes.count}>
                {total} {total === 1 ? 'option' : 'options'}
              </span>
            </div>
          </div>

          {pipelinesError && (
            <Typography className={classes.count} style={{ marginBottom: 12 }}>
              Control's requests (new environments and the like) could not be
              loaded just now, so only templates are listed.
            </Typography>
          )}

          {total === 0 ? (
            <EmptyState
              missing="content"
              title="No templates match"
              description="Try a different search or clear the filters."
            />
          ) : (
            <Grid container spacing={2}>
              {visibleRequests.map(p => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`request/${p.id}`}>
                  <Card className={classes.card}>
                    <div className={classes.cardRule} />
                    <div className={classes.cardHeader}>
                      <span className={classes.cardType}>{REQUEST_TYPE}</span>
                      <Typography className={classes.cardTitle}>
                        {p.title}
                      </Typography>
                    </div>
                    <CardContent className={classes.cardBody}>
                      <Typography className={classes.desc}>
                        {p.summary}
                      </Typography>
                      {(p.clouds?.length ?? 0) > 0 && (
                        <Box className={classes.meta}>
                          {p.clouds!.map(cloud => (
                            <Chip key={cloud} size="small" label={cloud} />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                    <div className={classes.actions}>
                      <span className={classes.owner}>
                        <span className={classes.ownerName}>
                          {/* Who answers it, since a request has no owner
                                group of its own. */}
                          {p.approval?.policy === 'devops'
                            ? 'Reviewed by DevOps'
                            : 'Via Control'}
                        </span>
                      </span>
                      <Link
                        to={`/create/templates/${encodeURIComponent(p.id)}`}
                        underline="none"
                      >
                        <Button size="small" variant="outlined">
                          Choose
                        </Button>
                      </Link>
                    </div>
                  </Card>
                </Grid>
              ))}
              {visible.map(t => {
                const name = t.metadata.name;
                const ns = t.metadata.namespace ?? 'default';
                const title = t.metadata.title ?? name;
                const desc = t.metadata.description ?? '';
                const tType = str(t.spec?.type);
                const tOwner = shortRef(t.spec?.owner);
                const tags = t.metadata.tags ?? [];
                const starred = isStarredEntity(t);
                // Stock card links to the template's own TechDocs when the
                // annotation is present.
                const docsUrl = t.metadata.annotations?.[
                  'backstage.io/techdocs-ref'
                ]
                  ? `/docs/${ns}/template/${name}`
                  : '';
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`${ns}/${name}`}>
                    <Card className={classes.card}>
                      <div className={classes.cardRule} />
                      {/* Same structure as the stock card — type above the
                            name, actions top-right — without the filled
                            banner. */}
                      <div className={classes.cardHeader}>
                        <span className={classes.cardType}>
                          {tType || 'template'}
                        </span>
                        <Typography className={classes.cardTitle}>
                          {title}
                        </Typography>
                        <div className={classes.headerIcons}>
                          {docsUrl && (
                            <Link to={docsUrl} underline="none">
                              <Tooltip title="View TechDocs" placement="top">
                                <IconButton
                                  size="small"
                                  className={classes.headerIcon}
                                  aria-label="View TechDocs"
                                >
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Link>
                          )}
                          <Tooltip
                            title={starred ? 'Unstar' : 'Star'}
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              aria-label={starred ? 'Unstar' : 'Star'}
                              className={`${classes.headerIcon} ${
                                starred ? classes.starOn : ''
                              }`}
                              onClick={() => toggleStarredEntity(t)}
                            >
                              {starred ? (
                                <StarIcon fontSize="small" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>

                      <CardContent className={classes.cardBody}>
                        {desc && (
                          <Typography className={classes.desc}>
                            {desc}
                          </Typography>
                        )}
                        {tags.length > 0 && (
                          <Box className={classes.meta}>
                            {tags.slice(0, 4).map(tag => (
                              <Chip key={tag} size="small" label={tag} />
                            ))}
                          </Box>
                        )}
                      </CardContent>

                      {docsUrl && (
                        <div className={classes.docsRow}>
                          <DescriptionIcon
                            fontSize="small"
                            className={classes.dim}
                          />
                          <Link to={docsUrl}>View TechDocs</Link>
                        </div>
                      )}

                      <div className={classes.actions}>
                        <span className={classes.owner}>
                          <PersonIcon
                            fontSize="small"
                            className={classes.dim}
                          />
                          <span className={classes.ownerName}>
                            {tOwner || '\u2014'}
                          </span>
                        </span>
                        <Link
                          to={`/create/templates/${ns}/${name}`}
                          underline="none"
                        >
                          <Button size="small" variant="outlined">
                            Choose
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </>
  );
};
